import "./PlanLimits.scss";
import React, { createContext, useContext, useMemo, useState } from "react";
import { Button, BUTTON_THEME } from "App/core/Button";
import { DialogButtons, Dialog, DialogSection } from "../Dialog";
import { useAuth } from "App/components/Auth";
import {
  USAGE_EDITOR_TAB,
  USAGE_EDITOR_TAB_KEY,
} from "App/components/Usages/UsageEditor";
import { Link } from "react-router-dom";
import { parseSubscription } from "@highnote/server/src/core/shared-util";
import { PLANS } from "App/components/Plans/config";
import { PlanPicker } from "App/components/Plans/Plans";
import {
  EVENT_ID,
  SUBSCRIPTION_TIER,
} from "@highnote/server/src/core/entities";
import { useSegmentContext } from "App/modules/useSegment";

export enum LIMIT_TYPE {
  STORAGE = "storage",
  SPACES = "spaces",
  TRACKS = "tracks",
}

const limitTypeCTABtnLabelMap = {
  [LIMIT_TYPE.STORAGE]: "Upgrade",
  [LIMIT_TYPE.SPACES]: "Upgrade",
  [LIMIT_TYPE.TRACKS]: "Upgrade",
};
const limitTypeManageBtnLabelMap = {
  [LIMIT_TYPE.STORAGE]: "Manage Storage",
  [LIMIT_TYPE.SPACES]: "Manage Spaces",
  [LIMIT_TYPE.TRACKS]: "Manage Tracks",
};

const limitTypeEditorTabMap = {
  [LIMIT_TYPE.STORAGE]: USAGE_EDITOR_TAB.STORAGE,
  [LIMIT_TYPE.SPACES]: USAGE_EDITOR_TAB.SPACES,
  [LIMIT_TYPE.TRACKS]: USAGE_EDITOR_TAB.TRACK_VERSIONS,
};

type PlanLimitsContextValue = {
  title: string;
  subtitle: string;
  hasReachedStorageLimit: boolean;
  hasReachedSpaceLimit: boolean;
  hasReachedTrackVersionsLimit: boolean;
  showPlanLimitsDialog: (type?: LIMIT_TYPE) => void;
  showPlanPickerDialog: () => void;
};

const PlanLimitsContext = createContext<PlanLimitsContextValue>({
  title: "",
  subtitle: "",
  hasReachedStorageLimit: false,
  hasReachedSpaceLimit: false,
  hasReachedTrackVersionsLimit: false,
  showPlanLimitsDialog: () => {},
  showPlanPickerDialog: () => {},
});

export const PlanLimitsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { trackEvent } = useSegmentContext();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [limitTypeToShow, setLimitTypeToShow] = useState<LIMIT_TYPE>(
    LIMIT_TYPE.TRACKS,
  );
  const [isPlanPickerOpen, setPlanPickerOpen] = useState<boolean>(false);
  const { storageLimit, activeSpacesLimit, trackVersionsLimit, user } =
    useAuth();
  const { tier } = parseSubscription(user?.subscriptions?.active);
  const plan = PLANS.find((p) => p.tier === tier);

  const value = useMemo(() => {
    const { storageUsed, activeSpacesUsed, trackVersionsUsed } = user || {
      storageUsed: 0,
      spacesUsed: 0,
    };

    const hasReachedStorageLimit = storageUsed >= storageLimit;
    const hasReachedSpaceLimit = activeSpacesUsed >= activeSpacesLimit;
    const hasReachedTrackVersionsLimit =
      trackVersionsUsed >= trackVersionsLimit;

    return {
      title: "",
      subtitle: "",
      hasReachedStorageLimit,
      hasReachedSpaceLimit,
      hasReachedTrackVersionsLimit,
      showPlanLimitsDialog: (typeToShow: LIMIT_TYPE) => {
        if (!user) {
          return;
        }
        setLimitTypeToShow(typeToShow);
        setDialogOpen(true);
      },
      showPlanPickerDialog: () => setPlanPickerOpen(true),
    };
  }, [user, storageLimit, activeSpacesLimit, plan?.name, trackVersionsLimit]);

  const memoizedLimitType = useMemo(() => {
    if (value.hasReachedTrackVersionsLimit) {
      return LIMIT_TYPE.TRACKS;
    }

    if (value.hasReachedSpaceLimit) {
      return LIMIT_TYPE.SPACES;
    }

    return LIMIT_TYPE.STORAGE;
  }, [value]);

  const storageLimitValue = useMemo(() => {
    // gets normalized storage limit
    const isProd = process.env.DEPLOY_ENV === "production";

    if (isProd) {
      if (tier === SUBSCRIPTION_TIER.STUDIO) return "5TB";
      if (tier === SUBSCRIPTION_TIER.INDIE || tier === SUBSCRIPTION_TIER.PRO)
        return "1TB";
      if (tier === SUBSCRIPTION_TIER.FREE) return "50GB";
    }

    if (tier === SUBSCRIPTION_TIER.FREE) return "10MB";
    if (tier === SUBSCRIPTION_TIER.INDIE || tier === SUBSCRIPTION_TIER.PRO)
      return "20MB";
  }, [tier]);

  let limitType = memoizedLimitType;

  if (limitTypeToShow) limitType = limitTypeToShow;

  value.title = useMemo(() => {
    switch (limitType) {
      case LIMIT_TYPE.SPACES:
        return "Upgrade to share more Spaces";
      case LIMIT_TYPE.TRACKS:
        return "Starter";
      case LIMIT_TYPE.STORAGE:
        return "Upgrade for more storage";
    }
  }, [limitType]);

  value.subtitle = useMemo(() => {
    const suffix = tier === SUBSCRIPTION_TIER.FREE ? " free" : "";
    if (limitType === LIMIT_TYPE.TRACKS) {
      return `You are using ${user?.trackVersionsUsed || 0} of your ${trackVersionsLimit} tracks available on your starter plan.`;
    }
    if (limitType === LIMIT_TYPE.STORAGE) {
      return `You’ve used your max ${storageLimitValue} of ${suffix} storage.`;
    }
    if (limitType === LIMIT_TYPE.SPACES) {
      return `You’ve used your ${activeSpacesLimit}${suffix} shared Space`;
    }
  }, [
    tier,
    limitType,
    trackVersionsLimit,
    storageLimitValue,
    activeSpacesLimit,
    user?.trackVersionsUsed,
  ]);

  const onPlanPickerDialogClose = () => {
    if (limitType === LIMIT_TYPE.TRACKS) {
      trackEvent(EVENT_ID.TRACK_LIMIT_REACHED_DISMISSED_PAYWALL);
    }
    setPlanPickerOpen(false);
  };

  const onUpgradeClick = () => {
    if (limitType === LIMIT_TYPE.TRACKS) {
      trackEvent(EVENT_ID.TRACK_LIMIT_REACHED_CLICKED_UPGRADE);
    }
  };

  const handleDialogClose = () => {
    if (limitType === LIMIT_TYPE.TRACKS) {
      trackEvent(EVENT_ID.TRACK_LIMIT_REACHED_DISMISSED_PAYWALL);
    }
    setDialogOpen(false);
  };

  const handleUpgradeClick = async () => {
    if (limitType === LIMIT_TYPE.TRACKS) {
      trackEvent(EVENT_ID.TRACK_LIMIT_REACHED_VIEWED_PAYWALL);
    }
    setPlanPickerOpen(true);
  };

  const handleManageButtonClick = () => {
    if (limitType === LIMIT_TYPE.TRACKS) {
      trackEvent(EVENT_ID.TRACK_LIMIT_REACHED_CLICKED_MANAGE_TRACKS);
    }
  };

  return (
    <PlanLimitsContext.Provider value={value}>
      {children}

      <Dialog
        className="PlanPicker-dialog"
        title={"Upgrade Now"}
        open={isPlanPickerOpen}
        onClose={onPlanPickerDialogClose}
      >
        <PlanPicker onUpgradeClick={onUpgradeClick} />
      </Dialog>

      <Dialog
        className="PlanLimits-dialog"
        open={isDialogOpen}
        onClose={handleDialogClose}
      >
        <div className="PlanLimits-dialog-hero" />
        <DialogSection>
          <p className="heading">
            <strong>{value.title}</strong>
          </p>
          <p>{value.subtitle}</p>
        </DialogSection>
        <DialogButtons>
          <Button theme={BUTTON_THEME.CTA} onClick={handleUpgradeClick}>
            {limitTypeCTABtnLabelMap[limitType]}
          </Button>
          {Object.values(LIMIT_TYPE).map((type) => {
            if (type !== limitType) {
              return null;
            }
            return (
              <Link
                key={type}
                to={`${location.pathname}?${USAGE_EDITOR_TAB_KEY}=${limitTypeEditorTabMap[type]}`}
              >
                <Button
                  theme={BUTTON_THEME.SECONDARY}
                  onClick={handleManageButtonClick}
                >
                  {limitTypeManageBtnLabelMap[type]}
                </Button>
              </Link>
            );
          })}
        </DialogButtons>
      </Dialog>
    </PlanLimitsContext.Provider>
  );
};

export const usePlanLimitsContext = () => useContext(PlanLimitsContext);
